import { onMounted, shallowRef, type Ref } from 'vue'

export interface NavigationItem {
  name: string
  url?: string
  flyout?: string | null
  css_classes?: string
  link_color?: string
}

let pending: Promise<NavigationItem[]> | undefined

/**
 * One request per page load, shared by every caller. The header renders
 * both the desktop bar and the mobile menu and lets CSS pick one, so a server
 * render's viewport guess cannot put the wrong one on screen (VIP-12562);
 * without sharing, that would fetch the same navigation twice.
 */
const loadNavigationItems = (): Promise<NavigationItem[]> => {
  pending ??= import('@/services/ApiController')
    .then(({ default: ApiController }) => ApiController.fetchNavigation())
    .then((items: unknown) =>
      Array.isArray(items) ? (items as NavigationItem[]) : []
    )
    .catch((error: unknown) => {
      // a failed request must not stick: the next mount tries again
      pending = undefined
      throw error
    })

  return pending
}

/** The CMS navigation, fetched once the component has mounted. */
export const useNavigationItems = (): {
  navigationItems: Ref<NavigationItem[]>
} => {
  const navigationItems = shallowRef<NavigationItem[]>([])

  onMounted(async () => {
    const startsRequest = !pending
    try {
      navigationItems.value = await loadNavigationItems()
    } catch (error) {
      // one failed request, one error report: only the caller that sent it
      // passes it on to the app's error handler
      if (startsRequest) throw error
    }
  })

  return { navigationItems }
}
